/**
 * StripeConnectAccordion
 * Stripe Connect section for coaching session create/edit forms.
 * Only shows for paid sessions and preserves form data during connection flow.
 */

import { Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import type { ConnectStatus } from "@/features/settings/services/earnings.service"

type StripeConnectAccordionProps = {
  connectStatus?: ConnectStatus
  isLoading: boolean
  isConnecting: boolean
  isDisconnecting: boolean
  isManaging: boolean
  onConnect: () => void
  onDisconnect: () => void
  onManage: () => void
}

export function StripeConnectAccordion({
  connectStatus,
  isLoading,
  isConnecting,
  isDisconnecting,
  isManaging,
  onConnect,
  onDisconnect,
  onManage,
}: StripeConnectAccordionProps) {
  if (isLoading) {
    return null
  }

  const isConnected = connectStatus?.connected ?? false
  const isChargeReady = connectStatus?.destinationChargeReady !== false

  if (isConnected) {
    return (
      <div className="rounded-lg space-y-4 mt-4">
        <div className="flex flex-col md:flex-row justify-between gap-3">
          <div className="space-x-3">
            <span className="text-[15px] font-semibold text-primary leading-6.25">
              Stripe Account
            </span>
            <span className="text-[13px] text-primary bg-olive px-2 py-1 rounded-sm leading-5">
              Connected
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              size="sm"
              className="uppercase py-3 px-10 border-[#F2F0EB] text-sm font-semibold text-primary flex-1 sm:flex-none"
              onClick={onManage}
              disabled={isManaging}
            >
              {isManaging ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Manage</>}
            </Button>
            <Button
              variant="primary-terracotta"
              size="sm"
              className="uppercase text-sm font-semibold flex-1 sm:flex-none py-3 px-10"
              onClick={onDisconnect}
              disabled={isDisconnecting}
            >
              {isDisconnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Disconnect"}
            </Button>
          </div>
        </div>

        {!isChargeReady && (
          <p className="text-[13px] text-amber-400">
            Paid offerings are paused until your Stripe account is fully ready for charges and
            payouts.
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="rounded-lg space-y-4 mt-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <span className="text-[15px] font-semibold text-primary leading-6.25">Stripe Account</span>
        <Button
          variant="primary"
          size="sm"
          className="uppercase py-3"
          onClick={onConnect}
          isLoading={isConnecting}
        >
          Connect with Stripe
        </Button>
      </div>

      <p className="text-[15px] text-primary leading-[25px] opacity-70">
        Takes about 5 minutes. This is how money from <br className="hidden md:block"></br> your
        clients gets to your bank account.
      </p>
    </div>
  )
}
